import Input from '../../../components/core/input'
import {SimpleBlueButton} from '../../../components/core/button'
import ButtonLoader from '../../../components/core/button/ButtonLoader'
import {Form, Formik} from 'formik'
import {useMemo, useState} from 'react'
import {useDispatch} from 'react-redux'
import integrationTypes from '../../../data/integrationTypes.json'
import LimitManager from '../../../utils/limitManager'
import CustomSelect from '../../../components/core/customSelect'
import {
    StatusIoIcon,
    StatuspageIoIcon
} from '../../../icons'
import {statuspageIoSchema, statusIoSchema} from '../../../utils/validation'
import {addNewIntegration} from '../../../stores/integration'
import {trimStringValues} from '../../../utils/text'
import RenderedForPaid from '../../../components/pages/RenderedForPaid'
import classNames from 'classnames'
import {useNamespaceTranslations} from '../../../utils/hooks/useNamespacesTranslations'

const integrationTypeElement = (value, label, isdisabled, icon) => {
    return {value, label, isdisabled, icon}
}

const isSameType = (value, name) => value === name

const fieldNames = {
    type: 'type'
}

const statuspageIoInputs = text => ([
    {
        name:'name',
        type: 'text',
        placeholder: text.name,
        infoText: text.name_info
    },
    {
        name:'api_key',
        type: 'text',
        placeholder:'API Key',
        infoText: text.api_key_info.replace(':value', text.statuspageIo)
    },
    {
        name:'page_id',
        type: 'text',
        placeholder:'Page ID',
        infoText: text.page_id_info.replace(':value', text.statuspageIo)
    }
])

const statusIoInputs = text => ([
    {
        name:'name',
        type: 'text',
        placeholder: text.name,
        infoText: text.name_info
    },
    {
        name:'api_id',
        type: 'text',
        placeholder:'API ID',
        infoText: text.api_id_info.replace(':value', text.statusIo)
    },
    {
        name:'api_key',
        type: 'text',
        placeholder:'API Key',
        infoText: text.api_key_info.replace(':value', text.statusIo)
    },
    {
        name:'page_id',
        type: 'text',
        placeholder:'Page ID',
        infoText: text.page_id_info.replace(':value', text.statusIo)
    }
])

const INTEGRATION_LOOKUP = text => ({
    [integrationTypes.types.statuspageIo]: statuspageIoInputs(text),
    [integrationTypes.types.statusIo]: statusIoInputs(text)
})

const VALIDATION_LOOKUP = {
    [integrationTypes.types.statuspageIo]: statuspageIoSchema(),
    [integrationTypes.types.statusIo]: statusIoSchema()
}

const INITIAL_VALUES_LOOKUP = {
    [integrationTypes.types.statuspageIo]: {
        name: '',
        api_key: '',
        page_id: ''
    },
    [integrationTypes.types.statusIo]: {
        name: '',
        api_id: '',
        api_key: '',
        page_id: ''
    }
}

const AddIntegration = ({
    closePopup,
    getCurrentIntegrationCount,
    type,
    isDarkMode,
    setType,
    zapierTypeIcons,
    isZapier,
    integrationOptions,
    isTypeSelected,
    isIntegrationDisabled
}) => {
    const text = useNamespaceTranslations('integrations')
    const usageLimits = useNamespaceTranslations('usage-limits')
    const zapierTypes = useNamespaceTranslations('zapier-types')
    const mobileWidth = 576
    const isMobile = window.innerWidth < mobileWidth
    const dispatch = useDispatch()
    const integrationTypeOptions = useMemo(() => {
        return [
            integrationTypeElement(integrationTypes.types.statuspageIo, 'Statuspage.Io', isIntegrationDisabled, <StatuspageIoIcon />),
            integrationTypeElement(integrationTypes.types.statusIo, 'Status.io', isIntegrationDisabled, <StatusIoIcon width={20} height={20} />)
        ].concat(zapierTypes.types.map(zap => integrationTypeElement(zap.type, `${zap.short_label} (${text.zapier})`, isIntegrationDisabled, zapierTypeIcons[zap.type])))
    }, [isIntegrationDisabled])
    const [active, setActive] = useState(null)
    const [selectedIntegration, setSelectedIntegration] = useState(integrationTypeOptions.find(item => item.value === type) || null)
    const initialFormValues = {
        type: selectedIntegration,
        ...INITIAL_VALUES_LOOKUP[selectedIntegration?.value]
    }

    const types = {
        [integrationTypes.types.statuspageIo]: {
            limit: LimitManager.getIntegrationLimit(usageLimits),
            key: usageLimits.limit_keys.integration_count.limit_key
        },
        [integrationTypes.types.statusIo]: {
            limit: LimitManager.getIntegrationLimit(usageLimits),
            key: usageLimits.limit_keys.integration_count.limit_key
        }
    }

    const handleInfo = (index, state) => {
        state ? setActive(index) : setActive(null)
    }

    const addIntegration = async(values, {setFieldError, resetForm}) => {
        if (isZapier) {
            window.open(zapierTypes?.types?.find(item => item?.type === type)?.value, '_blank')
            closePopup()
            return
        }
        const success = await dispatch(addNewIntegration(trimStringValues({
            ...values,
            type: values.type.value
        }), setFieldError, resetForm))
        if (success) {
            closePopup(true)
        }
        return true
    }

    const getPaidText = values => {
        const value = values.type?.value
        let data = {}

        Object.keys(types).forEach(item => {
            if (isSameType(value, item)) {
                data = {
                    integrationLimit: types[item].limit,
                    integrationCount: LimitManager.getLimitDiff(types[item].key, getCurrentIntegrationCount(value))
                }
            }
        })

        return data.integrationCount > 0 && text.integration_limit
            .replace(':integration_limit', data.integrationLimit)
            .replace(':integration_type', values.type.label)
            .replace(':integration_count', data.integrationCount)
    }

    const isExceeded = values => {
        const value = values.type?.value
        let condition
        Object.keys(types).forEach(item => {
            if (isSameType(value, item)) {
                condition = types[item].limit === getCurrentIntegrationCount(item)
            }
        })

        return condition
    }

    const checkSchema = (name, data, setFieldValue, setErrors, resetForm) => {
        resetForm(initialFormValues)
        setFieldValue(name, data)
        setSelectedIntegration(data)
        setErrors({})
        setType(data.value)
    }

    return isTypeSelected ? (
        <Formik
            initialValues={initialFormValues}
            enableReinitialize={true}
            onSubmit={addIntegration}
            validationSchema={VALIDATION_LOOKUP[selectedIntegration?.value]}
            validateOnBlur={false}
            validateOnChange={false}>
            {({values, errors, handleSubmit, setFieldValue, handleChange, isSubmitting, setErrors, resetForm}) => {
                return  (
                    <Form onSubmit={handleSubmit} className="modal-body">
                        {((values.type?.value && !isZapier) || (isZapier && isIntegrationDisabled)) &&
                        <RenderedForPaid
                            forPaidText={getPaidText(values) || text.for_paid}
                            isExceeded={isExceeded(values)}
                            isTextWithLimits={!!getPaidText(values)}
                        />}
                        <CustomSelect
                            placeholder={text.type}
                            onChange={(name, data) => checkSchema(name, data, setFieldValue, setErrors, resetForm)}
                            infoText={text.type_info}
                            name={fieldNames.type}
                            value={values.type}
                            options={integrationTypeOptions}
                            defaultFirst={true}
                            checkboxClassName="multiselect"
                            activeInfo={active === fieldNames.type}
                            handleInfo={(state) => handleInfo(fieldNames.type, state)}
                            error={errors.type}
                        />
                        {
                            INTEGRATION_LOOKUP(text)?.[values.type?.value]?.map((item, index) => {
                                return  <Input
                                    key={index + fieldNames.type}
                                    type={item.type}
                                    infoText={item.infoText}
                                    placeholder={item.placeholder}
                                    name={item.name}
                                    value={values[item.name]}
                                    onChange={handleChange}
                                    inputIsDisabled={isExceeded(values)}
                                    activeInfo={active === item.name}
                                    handleInfo={(state) => handleInfo(item.name, state)}
                                    error={errors[item.name]}
                                />
                            }
                            )
                        }
                        <SimpleBlueButton
                            type="submit"
                            disabled={isSubmitting || isExceeded(values) || isIntegrationDisabled}>
                            {isSubmitting && <ButtonLoader/>}
                            {text.submit}
                        </SimpleBlueButton>
                    </Form>
                )
            }}
        </Formik>
    ) : (
        <div className={classNames('integration-content', {
            dark: isDarkMode
        })}>
            {integrationOptions.map(item => (
                <div className="card" key={item.type} onClick={() => {
                    setType(item.type)
                    setSelectedIntegration(integrationTypeOptions.find(integration => integration.value === item.type))
                }}>
                    <span className="icon">{item.icon}</span>
                    <span className="label">{item.name}</span>
                </div>
            ))}
            {zapierTypes.types.map(item => (
                <div
                    onClick={() => {
                        setType(item.type)
                        setSelectedIntegration(integrationTypeOptions.find(integration => integration.value === item.type))
                    }}
                    className="card"
                    key={item.type}
                >
                    <span className="icon">{zapierTypeIcons[item.type]}</span>
                    <span className="label">{isMobile ? item.short_label : item.label}</span>
                    <span className="zap">{text.zapier}</span>
                </div>
            ))}
        </div>
    )
}

export default AddIntegration
